import { useCallback, useEffect, useRef, useState } from "react";

// --- constants & types -------------------------------------------------------

const SUPPORTED_CODECS = {
  AV1: 3,
  VP9: 2,
  H264: 1,
} as const;

const FALLBACK_CODEC: SupportedCodecKey = "H264";
export type SupportedCodecKey = keyof typeof SUPPORTED_CODECS;
export const supportedCodecKeys = Object.keys(SUPPORTED_CODECS) as SupportedCodecKey[];

export type CodecWithHardware = { codec: SupportedCodecKey; hardware: boolean };

const VIDEO_CODEC_PREFIX = "video/";
const VIDEO_CODEC_PREFIX_LENGTH = VIDEO_CODEC_PREFIX.length;

// Default probe config for MediaCapabilities (tweak if needed)
const DEFAULT_PROBE = {
  width: 1920,
  height: 1080,
  framerate: 30,
  bitrate: 2_000_000,
};


// --- tiny helpers ------------------------------------------------------------

export const cleanVideoMimeType = (mimeType: string): SupportedCodecKey | null => {
  if (!mimeType.startsWith(VIDEO_CODEC_PREFIX)) return null;
  return mimeType.slice(VIDEO_CODEC_PREFIX_LENGTH).toUpperCase() as SupportedCodecKey;
};

const toContentType = (codec: SupportedCodecKey) => `video/${codec.toLowerCase()}`;

const hasMediaCapabilities =
  typeof navigator !== "undefined" &&
  "mediaCapabilities" in navigator &&
  typeof (navigator as any).mediaCapabilities?.decodingInfo === "function";

// cache per (codec + probe) so we don’t re-probe repeatedly
const hwCache = new Map<string, boolean>();
const hwKey = (
  c: SupportedCodecKey,
  p = DEFAULT_PROBE
) => `${c}:${p.width}x${p.height}@${p.framerate}:${p.bitrate}`;

const codecPreferenceSort = (a: SupportedCodecKey, b: SupportedCodecKey) =>
  SUPPORTED_CODECS[b] - SUPPORTED_CODECS[a];


// --- capabilities enumeration (sync, via RTP) --------------------------------

function getAllCodecCapabilities(): SupportedCodecKey[] {
  const caps = RTCRtpReceiver.getCapabilities?.("video");
  const codecCaps = caps?.codecs ?? [];
  const seen = new Set<SupportedCodecKey>();
  for (const c of codecCaps) {
    const k = cleanVideoMimeType(c.mimeType);
    if (k && supportedCodecKeys.includes(k)) seen.add(k);
  }
  return Array.from(seen);
}

// --- hardware (power-efficient) probing (async) ------------------------------

/**
 * Returns true if MediaCapabilities suggests decoding is power-efficient
 * for the given codec + probe config. Falls back to false if unsupported.
 */
async function isHardwareDecodingLikely(
  codec: SupportedCodecKey,
  probe = DEFAULT_PROBE
): Promise<boolean> {
  // Cache first
  const key = hwKey(codec, probe);
  if (hwCache.has(key)) return hwCache.get(key)!;

  // No MC API? Conservative fallback = false
  if (!hasMediaCapabilities) {
    hwCache.set(key, false);
    return false;
  }

  try {
    const contentType = toContentType(codec);
    // Some browsers require all fields present for type 'webrtc'
    const result = await (navigator as any).mediaCapabilities.decodingInfo({
      type: "webrtc",
      video: {
        contentType,
        width: probe.width,
        height: probe.height,
        framerate: probe.framerate,
        bitrate: probe.bitrate,
      },
    });

    // Heuristic: only call it "hardware" if supported AND powerEfficient.
    const hw = !!(result?.supported && result?.powerEfficient);
    hwCache.set(key, hw);
    return hw;
  } catch {
    // Be safe: on errors, assume not hardware-accelerated
    hwCache.set(key, false);
    return false;
  }
}

// --- public API: supported codecs WITH hardware flag -------------------------

/**
 * Enumerates supported codecs (per RTP capabilities), sorted by preference,
 * and annotates each with a best-effort hardware (power-efficient) hint.
 */
export async function getSupportedCodecsWithHardware(
  probe = DEFAULT_PROBE
): Promise<CodecWithHardware[]> {
  const supported = getAllCodecCapabilities().sort(codecPreferenceSort);
  const hardwareFlags = await Promise.all(
    supported.map((c) => isHardwareDecodingLikely(c, probe))
  );
  return supported.map((codec, i) => ({ codec, hardware: hardwareFlags[i] }));
}

let codecHwCache: CodecWithHardware[] | null = null;

export async function getCachedSupportedCodecsWithHardware(
  probe = DEFAULT_PROBE
): Promise<CodecWithHardware[]> {
  if (!codecHwCache) {
    codecHwCache = await getSupportedCodecsWithHardware(probe);

    if (codecHwCache.length === 0) {
      console.warn("[Codec] No supported codecs found for the given parameters");
    } else {
      console.log(
        "[Codec] Codecs with hardware flags:",
        codecHwCache.map((c) => `${c.codec}:${c.hardware ? "HW" : "SW"}`).join(", ")
      );
    }
  }
  return codecHwCache;
}

/**
 * Clears the cached supported codecs with hardware flags.
 */
export function clearCodecHardwareCache(): void {
  codecHwCache = null;
  hwCache.clear();
}

/**
 * Gets the preferred codec, prioritizing hardware-accelerated ones. Just returns the 
 * first power-efficient codec found, or falls back to the default if none available.
 */
export function getTheChosenCodec(codecs: CodecWithHardware[] | null): SupportedCodecKey {
  const chosen = (codecs && codecs.length > 0) ? codecs.find(c => c.hardware)?.codec : null;
  return chosen || FALLBACK_CODEC;
}

/**
 * Gets a list of codec names that are hardware-accelerated.
 */
export function getHardwareCodecNames(codecs: CodecWithHardware[] | null): SupportedCodecKey[] {
  const cdx = codecs?.filter(c => c.hardware).map(c => c.codec) || [];
  return cdx.length > 0 ? cdx : [FALLBACK_CODEC];
}

/**
 * a hook form of the cache, basically...
 */
export function useCodecCapabilitiesWithHardware() {
  const [codecs, setCodecs] = useState<CodecWithHardware[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);

  // prevent late state updates after unmount
  const abortedRef = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      // SSR / unsupported guard: if no window or no RTCRtpReceiver, return empty
      if (typeof window === "undefined" || typeof RTCRtpReceiver === "undefined") {
        if (!abortedRef.current) setCodecs([]);
        return;
      }
      const list = await getCachedSupportedCodecsWithHardware();
      if (!abortedRef.current) setCodecs(list);
    } catch (e) {
      if (!abortedRef.current) setError(e);
    } finally {
      if (!abortedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    abortedRef.current = false;
    load();
    return () => {
      abortedRef.current = true;
    };
  }, [load]);

  const refresh = useCallback(async () => {
    clearCodecHardwareCache();
    await load();
  }, [load]);

  return { codecs, loading, error, refresh };
}
